import {
  ComplexObject,
  Entity,
  EntityAction,
  EntityChangedEventArgs,
  EntityKey,
  EntityManager,
  EntityState,
  EntityType,
  MergeStrategy,
  MetadataStore,
  PropertyChangedEventArgs,
  SaveOptions,
} from 'breeze-client';
import { ImportResult, SaveResult } from 'breeze-client/src/entity-manager';
import * as _ from 'lodash-es';
import { Observable, Subject, combineLatest, defer, forkJoin, from, merge, of, throwError } from 'rxjs';
import {
  catchError,
  concatMap,
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  map,
  shareReplay,
  startWith,
  tap,
} from 'rxjs/operators';
import { EntityManagerProvider } from './entity-manager-provider';
import { EntitySelector, EntitySelectorConfig, EntityTypeLike, getEntities, getEntityType } from './entity-selector';
import { ObservablePropertyBag } from './observable-property-bag';
import { EntityPropertyPaths, Repository } from './repository';
import { UnitOfWorkHelper } from './unit-of-work-helper';

export type EntityValues<T extends Entity> = Partial<Omit<T, keyof Entity>>;
export type ComplexValues<T extends ComplexObject> = Partial<Omit<T, keyof ComplexObject>>;

export interface EntityFactory<T extends Entity> {
  create(initialValues?: EntityValues<T>, entityState?: EntityState): T;
}

export class GenericEntityFactory<T extends Entity, TUnitOfWork extends UnitOfWorkBase> implements EntityFactory<T> {
  constructor(private readonly entityTypeLike: EntityTypeLike<T>, protected readonly unitOfWork: TUnitOfWork) {}

  create(initialValues?: EntityValues<T>, entityState?: EntityState): T {
    return this.unitOfWork.createEntity(this.entityTypeLike, initialValues, entityState);
  }
}

export class SavedOrRejectedArgs {
  entities: Entity[];
  rejected: boolean;
}

type IShelveSet = Record<string, { entities: any; state: Record<string, any> }>;
type ShelvedEventArgs = { key: string; wasCleared: boolean };

export abstract class SaveHandlers {
  afterSaveEntities(unitOfWork: UnitOfWorkBase, saveResult: SaveResult): Observable<void> {
    return of(void 0);
  }
  beforeSaveEntity(unitOfWork: UnitOfWorkBase, entity: Entity): Observable<boolean> {
    return of(true);
  }
}

export abstract class UnitOfWorkBase {
  entityChanged$: Observable<EntityChangedEventArgs>;
  entityCacheChanged$: Observable<EntityChangedEventArgs>;
  entityPropertyChanged$: Observable<PropertyChangedEventArgs>;
  hasChanges$: Observable<boolean>;

  private static shelveSets: IShelveSet = {};

  readonly selector: EntitySelector;
  readonly state = new ObservablePropertyBag();

  private _manager: EntityManager;
  private shadowManager: EntityManager;
  private readonly committedSubject = new Subject<Entity[]>();
  private readonly rejectedSubject = new Subject<Entity[]>();
  private readonly clearedSubject = new Subject<void>();
  private readonly shelvedSubject = new Subject<ShelvedEventArgs>();
  private readonly unshelvedSubject = new Subject<ShelvedEventArgs>();

  constructor(
    private readonly entityManagerProvider: EntityManagerProvider,
    protected readonly helper: UnitOfWorkHelper
  ) {
    this.selector = new UnitOfWorkEntitySelector(this, this.manager.metadataStore);

    // EntityManager events
    const entityChanged = new Observable<EntityChangedEventArgs>((observer) => {
      const manager = this.manager;
      const subKey = manager.entityChanged.subscribe((args) => {
        observer.next(args);
      });

      return () => {
        manager.entityChanged.unsubscribe(subKey);
      };
    });
    this.entityChanged$ = entityChanged.pipe(
      filter((args) => {
        const relevantEntityActions = [EntityAction.EntityStateChange, EntityAction.PropertyChange];
        return _.includes(relevantEntityActions, args.entityAction);
      })
    );
    this.entityCacheChanged$ = entityChanged.pipe(
      filter((args) => {
        const relevantEntityActions = [
          EntityAction.Attach,
          EntityAction.Detach,
          EntityAction.AttachOnImport,
          EntityAction.AttachOnQuery,
          EntityAction.MergeOnImport,
          EntityAction.MergeOnQuery,
          EntityAction.MergeOnSave,
          EntityAction.Clear,
        ];
        return _.includes(relevantEntityActions, args.entityAction);
      })
    );
    this.entityPropertyChanged$ = entityChanged.pipe(
      filter((change) => change.entityAction === EntityAction.PropertyChange),
      map((change) => change.args)
    );

    const hasChangesChanged = new Observable<boolean>((observer) => {
      const manager = this.manager;
      const subKey = manager.hasChangesChanged.subscribe((args) => {
        observer.next(args.hasChanges);
      });

      return () => {
        manager.hasChangesChanged.unsubscribe(subKey);
      };
    });
    this.hasChanges$ = defer(() => {
      const manager = this.manager;
      return hasChangesChanged.pipe(startWith(manager.hasChanges()));
    }).pipe(distinctUntilChanged(), shareReplay({ bufferSize: 1, refCount: false }));
  }

  protected get manager(): EntityManager {
    if (!this._manager) {
      this._manager = this.entityManagerProvider.newManager();
    }
    return this._manager;
  }

  hasChanges(): boolean {
    return this.manager.hasChanges();
  }

  get serviceName(): string {
    return this.entityManagerProvider.serviceName;
  }

  get metadataStore(): MetadataStore {
    return this.manager.metadataStore;
  }

  get committed$(): Observable<Entity[]> {
    return this.committedSubject.asObservable();
  }

  get rejected$(): Observable<Entity[]> {
    return this.rejectedSubject.asObservable();
  }

  get cleared$(): Observable<void> {
    return this.clearedSubject.asObservable();
  }

  get shelved$(): Observable<ShelvedEventArgs> {
    return this.shelvedSubject.asObservable();
  }

  get unshelved$(): Observable<ShelvedEventArgs> {
    return this.unshelvedSubject.asObservable();
  }

  static deleteShelveSet(key: string): void {
    delete UnitOfWork.shelveSets[key];
  }

  createEntity<T extends Entity>(
    entityTypeLike: EntityTypeLike<T>,
    initialValues?: EntityValues<T>,
    entityState?: EntityState
  ): T {
    const entityType = getEntityType(entityTypeLike, this.manager.metadataStore);
    const inst = this.manager.createEntity(entityType, initialValues, entityState) as T;

    return inst;
  }

  exportEntities(entities?: Entity[], asString: boolean = false): string | Record<string, any> {
    return this.manager.exportEntities(entities, {
      asString,
      includeMetadata: false,
    });
  }

  importEntities(exportedData: string | Record<string, any>): ImportResult {
    return this.manager.importEntities(exportedData);
  }

  attachEntity<T extends Entity>(entity: T, options?: { entityState?: EntityState; mergeStrategy?: MergeStrategy }): T {
    return this.manager.attachEntity(entity, options?.entityState, options?.mergeStrategy) as T;
  }

  /** Restore deleted entity: set Added if Detached, or Unchanged if Deleted */
  undeleteEntity<T extends Entity>(entity: T): T {
    if (entity.entityAspect.entityState.isDetached()) {
      return this.attachEntity(entity, { entityState: EntityState.Added });
    } else if (entity.entityAspect.entityState.isDeleted()) {
      entity.entityAspect.rejectChanges();
      return entity;
    }
  }

  detachEntity(entity: Entity): boolean {
    return this.manager.detachEntity(entity);
  }

  getChanges(): Entity[] {
    return this.manager.getChanges();
  }

  getEntities<T extends Entity>(entityTypeLike?: EntityTypeLike<T>, includeDeleted?: boolean): T[] {
    return getEntities(this.manager, entityTypeLike, includeDeleted);
  }

  getEntity<T extends Entity>(entityTypeLike: EntityTypeLike<T>, ...keyValues: any[]): T;
  getEntity<T extends Entity>(entityKey: EntityKey): T;
  getEntity<T extends Entity>(entityKeyOrTypeLike: EntityTypeLike<T> | EntityKey, ...keyValues: any[]): T {
    if (entityKeyOrTypeLike instanceof EntityKey) {
      return this.manager.getEntityByKey(entityKeyOrTypeLike) as T;
    }

    const entityType = getEntityType(entityKeyOrTypeLike, this.metadataStore, true);
    if (!entityType) {
      // No type match
      return null;
    }
    const entityKey = new EntityKey(entityType, keyValues);
    return this.manager.getEntityByKey(entityKey) as T;
  }

  commit(entities?: Entity[], saveOptions?: SaveOptions): Observable<SaveResult> {
    return defer(() => {
      entities = entities || this.getChanges();
      return this.helper.busyService.busy$(
        'Saving data...',
        this.beforeSaveEntities(entities).pipe(
          concatMap((changes) => {
            changes = changes.filter((entity) => !!entity);
            return from(this.manager.saveChanges(changes, saveOptions)).pipe(
              concatMap((saveResult) => {
                if (this.helper.saveHandlers) {
                  return this.helper.saveHandlers.afterSaveEntities(this, saveResult).pipe(
                    first(),
                    map(() => saveResult)
                  );
                }
                return of(saveResult);
              }),
              tap((saveResult) => this.committedSubject.next(saveResult.entities))
            );
          })
        )
      );
    });
  }

  rollback(): void {
    const pendingChanges = this.manager.getChanges();
    this.manager.rejectChanges();
    this.rejectedSubject.next(pendingChanges);
  }

  rejectChanges(...entities: Entity[]): void {
    (entities || []).forEach((entity) => entity.entityAspect.rejectChanges());
    this.rejectedSubject.next(entities);
  }

  clear(clearState: boolean = false): void {
    this.manager.clear();
    if (clearState) {
      this.state.clear();
    }
    this.clearedSubject.next();
    this.helper.signalClear(this, this.manager);
  }

  shelve(key: string, clear: boolean = false): void {
    const data = {
      entities: this.manager.exportEntities(null, {
        asString: false,
        includeMetadata: false,
      }),
      state: this.state.snapshot,
    };
    UnitOfWork.shelveSets[key] = data;
    if (clear) {
      this.clear(true);
    }

    this.shelvedSubject.next({
      key,
      wasCleared: clear,
    });
  }

  unshelve(key: string, clear: boolean = true): boolean {
    if (clear) {
      this.clear(true);
    }

    const data = UnitOfWork.shelveSets[key];
    if (!data) {
      return false;
    }

    this.manager.importEntities(data.entities);
    this.state.restore(data.state);

    // Delete the shelveSet
    delete UnitOfWork.shelveSets[key];

    this.unshelvedSubject.next({
      key: key,
      wasCleared: clear,
    });
    return true;
  }

  deleteEntities(...entities: Entity[]): void {
    (entities || []).forEach((entity) => entity?.entityAspect.setDeleted());
  }

  createShadowCopy(...entities: Entity[]): void {
    if (!this.shadowManager) {
      this.shadowManager = this.entityManagerProvider.newManager();
    }
    const bundle = this.exportEntities(entities);
    this.shadowManager.importEntities(bundle, {
      mergeAdds: true,
      mergeStrategy: MergeStrategy.OverwriteChanges,
    });
  }

  restoreShadowCopy(...entities: Entity[]): boolean {
    if (!this.shadowManager) {
      return false;
    }
    const shadowEntities = entities.map((entity) => this.shadowManager.getEntityByKey(entity.entityAspect.getKey()));
    const bundle = this.shadowManager.exportEntities(shadowEntities, {
      asString: false,
      includeMetadata: false,
    });
    this.manager.importEntities(bundle, {
      mergeAdds: true,
      mergeStrategy: MergeStrategy.OverwriteChanges,
    });
    shadowEntities.forEach((entity) => this.shadowManager.detachEntity(entity));
    return true;
  }

  createRepository<T extends Entity>(
    entityTypeLike: EntityTypeLike<T>,
    resourceName: string,
    expand?: EntityPropertyPaths<T>,
    isCached: boolean = false
  ): Repository<T> {
    return this.helper.createRepository<T>(this.manager, entityTypeLike, expand, resourceName, isCached);
  }

  createFactory<T extends Entity>(entityTypeLike: EntityTypeLike<T>): EntityFactory<T> {
    return new GenericEntityFactory<T, UnitOfWorkBase>(entityTypeLike, this);
  }

  private beforeSaveEntities(changes: Entity[]): Observable<Entity[]> {
    if (!changes || changes.length === 0) {
      return of([]);
    }

    return forkJoin(
      changes.map((entity) => {
        if (this.helper.saveHandlers) {
          return this.helper.saveHandlers.beforeSaveEntity(this, entity).pipe(
            first(),
            map((result) => (result ? entity : null))
          );
        }
        return of(entity);
      })
    );
  }
}

export abstract class UnitOfWork<TLookupTypes> extends UnitOfWorkBase {
  readonly lookupTypes$: Observable<TLookupTypes>;

  constructor(entityManagerProvider: EntityManagerProvider, helper: UnitOfWorkHelper) {
    super(entityManagerProvider, helper);

    this.lookupTypes$ = combineLatest([entityManagerProvider.lookupTypes$, this.cleared$.pipe(startWith(false))]).pipe(
      map(([lookupTypes]) => {
        this.importEntities(lookupTypes);
        return this.lookupTypes();
      }),
      shareReplay({ bufferSize: 1, refCount: false })
    );
  }

  protected abstract lookupTypes(): TLookupTypes;
}

class UnitOfWorkEntitySelector implements EntitySelector {
  private readonly selectors: Record<string, Observable<any>> = {};

  constructor(private readonly unitOfWork: UnitOfWorkBase, private readonly metadataStore: MetadataStore) {}

  getEntities$<T extends Entity>(entityTypeLike?: EntityTypeLike<T>, config?: EntitySelectorConfig): Observable<T[]> {
    // Defaults
    config = {
      includeDeleted: false,
      emitOnModify: false,
      ...(config || {}),
    };
    const entityType = entityTypeLike && getEntityType(entityTypeLike, this.metadataStore);
    const selectorName = `${entityType ? entityType.shortName : ''}${btoa(JSON.stringify(config))}$`;

    return (
      this.selectors[selectorName] ||
      (this.selectors[selectorName] = this.createSelector<T>(selectorName, entityType, config))
    );
  }

  private createSelector<T extends Entity>(
    name: string,
    entityType: EntityType,
    config: EntitySelectorConfig
  ): Observable<T[]> {
    return defer(() =>
      (config.emitOnModify
        ? merge(this.unitOfWork.entityChanged$, this.unitOfWork.entityCacheChanged$)
        : this.unitOfWork.entityCacheChanged$
      ).pipe(
        catchError((err) => {
          // Burn selector if it errors out
          this.selectors[name] = null;
          return throwError(() => err);
        }),
        filter((args) => {
          if (!entityType || !args.entity || args.entity.entityType.isSubtypeOf(entityType)) {
            return true;
          }
          return false;
        }),
        debounceTime(200),
        map(() => {
          const entities = this.unitOfWork.getEntities<T>(entityType, config.includeDeleted);
          // console.log(`Entity selector ${name}: emit ${entities.length} entitites`);
          return entities;
        }),
        startWith(this.unitOfWork.getEntities<T>(entityType, config.includeDeleted))
      )
    ).pipe(shareReplay({ bufferSize: 1, refCount: true }));
  }
}
